import { CrossSectionCanvas } from '@pages/cross-section/shared/CrossSectionCanvas';
import { showTekPointSoilSurvey, parseDataAndCalculateEndPoint } from '@pages/soil-survey/soil-survey-main';
import { SURVEY_TYPE_TITLES } from '@pages/soil-survey/soil-survey-constants';
import Konva from 'konva';
import { getTekPointImage } from '@shared/openlayers/points/TekPointsLayer';
import { ColorService } from '@shared/codelist/color-service';
import { createIsoSymbolCanvas, getIsoPointStyleFromCode } from '@shared/codelist/point-service';
import { RGBAColor } from '@shared/codelist/types';

function drawModel(opts: {
    model: any;
    csCanvas: CrossSectionCanvas;
    hiddenLineIds: Array<string | number>;
    onClickCallback: (pipe, x, y) => void;
    type: 'model' | 'terrainModel';
}) {
    const { model, csCanvas, onClickCallback, hiddenLineIds } = opts;
    const center = csCanvas.getCenter();
    const group = new Konva.Group({
        id: 'model_' + model.id,
    });

    const canvasOptions = csCanvas.canvasOptions;
    const lowestPoint = csCanvas.lowestPoint;

    const surfaceCode = typeof model.surfaceCode !== 'undefined' && model.surfaceCode !== null ? model.surfaceCode : '';
    const terrainCode = typeof model.terrainCode !== 'undefined' && model.terrainCode !== null ? model.terrainCode : '';

    let color: RGBAColor;
    const modelColorParts: string[] = model.color.split(',');
    const modelColorAsRGBA: RGBAColor = { r: parseInt(modelColorParts[0], 10), g: parseInt(modelColorParts[1], 10), b: parseInt(modelColorParts[2], 10), a: 1 };
    if (surfaceCode.length > 0) {
        color = ColorService.getSurfaceColorForInfraCode(surfaceCode, modelColorAsRGBA);
    } else if (terrainCode.length > 0) {
        color = ColorService.getSurfaceColorForInfraCode(terrainCode, modelColorAsRGBA);
    } else {
        color = { r: 0, g: 0, b: 0, a: 1 };
    }

    const colorStr = color.r + ',' + color.g + ',' + color.b;
    model.color = colorStr;

    model.lines.forEach(modelLine => {
        if (hiddenLineIds.includes(modelLine.id)) {
            return;
        }
        // These props are usually empty. Hide them unless some of them has some reasonable value
        const name = modelLine.name ?? '';
        const surfaceCode = typeof modelLine.surfaceCode !== 'undefined' && modelLine.surfaceCode !== null ? modelLine.surfaceCode : '';
        const terrainCode = typeof modelLine.terrainCode !== 'undefined' && modelLine.terrainCode !== null ? modelLine.terrainCode : '';
        let props;
        if (name.length > 0 || terrainCode.length > 0 || surfaceCode.length > 0) {
            props = { name: name, terrainCode: terrainCode, surfaceCode: surfaceCode };
        }

        modelLine.vectors.forEach(vector => {
            if (vector.points.length < 2) {
                return true;
            }

            const x0 = center + vector.points[0].x * canvasOptions.x_scale;
            const y0 = csCanvas.height - canvasOptions.bottom_margin + lowestPoint * canvasOptions.y_scale - vector.points[0].y * canvasOptions.y_scale + csCanvas.panY;
            const x1 = center + vector.points[1].x * canvasOptions.x_scale;
            const y1 = csCanvas.height - canvasOptions.bottom_margin + lowestPoint * canvasOptions.y_scale - vector.points[1].y * canvasOptions.y_scale + csCanvas.panY;

            if (x0 < canvasOptions.side_margin / 2 && x1 < canvasOptions.side_margin / 2) {
                return;
            } else if (x0 > csCanvas.width - canvasOptions.side_margin / 2 && x1 > csCanvas.width - canvasOptions.side_margin / 2) {
                return;
            } else if (y0 < canvasOptions.top_margin && y1 < canvasOptions.top_margin) {
                return;
            } else if (y0 > csCanvas.height - canvasOptions.bottom_margin && y1 > csCanvas.height - canvasOptions.bottom_margin) {
                return;
            }

            const xLen = x1 - x0;
            const yLen = y1 - y0;
            const lineLen = Math.sqrt(xLen * xLen + yLen * yLen);
            if (lineLen < 2) {
                return;
            }

            const line = new Konva.Line({
                name: 'model_' + model.modelId,
                points: [x0, y0, x1, y1],
                stroke: getRgb(colorStr),
                strokeWidth: model.strokeWidth,
                lineCap: 'round',
                lineJoin: 'round',
                listening: false,
            });

            // Set tooltip line a bit shorter than the real line -> from +10% start to -10% from end
            if (lineLen > 10) {
                const corx = xLen / 10;
                const cory = yLen / 10;

                const tooltipLine = new Konva.Line({
                    name: 'tooltipline_model',
                    points: [x0 + corx, y0 + cory, x1 - corx, y1 - cory],
                    stroke: getRgb(colorStr),
                    strokeWidth: 4,
                    lineCap: 'round',
                    lineJoin: 'round',
                    listening: true,
                    opacity: 0,
                });
                tooltipLine.on('click tap', function () {
                    line.strokeWidth(line.strokeWidth() + 2);
                    showLineTooltip(opts.model.name, name, x0, y0, csCanvas, true);
                });
                tooltipLine.on('mouseover', function () {
                    line.strokeWidth(line.strokeWidth() + 2);
                    showLineTooltip(opts.model.name, name, x0, y0, csCanvas, false);
                });
                tooltipLine.on('mouseout', function () {
                    line.strokeWidth(line.strokeWidth() - 2);
                    csCanvas.clearHoverTooltips();
                    csCanvas.contentLayer.draw();
                });
                group.add(tooltipLine);
            }
            const vertex0Hitbox = new Konva.Circle({
                name: 'model_' + model.modelId,
                x: x0,
                y: y0,
                stroke: 'transparent',
                radius: 5,
            });
            const vertex1Hitbox = new Konva.Circle({
                name: 'model_' + model.modelId,
                x: x1,
                y: y1,
                stroke: 'transparent',
                radius: 5,
            });

            if (opts.type === 'terrainModel') {
                line.dash([9, 9]);
            }
            vertex0Hitbox.on('click tap', function () {
                onClickCallback(vector.points[0], x0, y0);
            });
            vertex1Hitbox.on('click tap', function () {
                onClickCallback(vector.points[1], x1, y1);
            });

            vertex0Hitbox.on('mouseover', function () {
                showVertexTooltip(vector.points[0], x0, y0, model.name, modelLine.name, props, csCanvas);
                csCanvas.drawSnapCursor(true, x0, y0);
            });
            vertex1Hitbox.on('mouseover', function () {
                showVertexTooltip(vector.points[1], x1, y1, model.name, modelLine.name, props, csCanvas);
                csCanvas.drawSnapCursor(true, x1, y1);
            });

            vertex0Hitbox.on('mouseout', csCanvas.clearHoverTooltips);
            vertex1Hitbox.on('mouseout', csCanvas.clearHoverTooltips);
            group.add(vertex0Hitbox);
            group.add(vertex1Hitbox);
            group.add(line);
        });
    });

    model.points.forEach(modelPoint => {
        const x = csCanvas.metersToScreenCoordsX(modelPoint.point.x);
        const y = csCanvas.metersToScreenCoordsY(modelPoint.point.y);

        if (x < canvasOptions.side_margin / 2 || x > csCanvas.width - canvasOptions.side_margin / 2) {
            return;
        }

        const originalPointColor: string = modelPoint.color || '0,0,0';
        const originalPointColorRGBA: RGBAColor = {
            r: parseInt(originalPointColor.split(',')[0], 10),
            g: parseInt(originalPointColor.split(',')[1], 10),
            b: parseInt(originalPointColor.split(',')[2], 10),
            a: 1,
        };

        // Code is present if the model is an actual point like in GT files.
        // Terrain coding is present in line models as well as lines that are present in surfaces.
        const pointColor: RGBAColor = ColorService.getPointColorForCode(modelPoint.props?.Code || modelPoint.props?.terrainCoding || '', originalPointColorRGBA);
        const color: string = pointColor.r + ',' + pointColor.g + ',' + pointColor.b;

        let shape;
        if (modelPoint.props && modelPoint.props.tutkimustapa) {
            drawTekBar({ modelPoint, model, x, y, color, csCanvas, group });
            shape = null;
        } else {
            if (y < canvasOptions.top_margin || y > csCanvas.height - canvasOptions.bottom_margin) {
                return;
            }

            const pointStyleCode = modelPoint.props?.code ?? modelPoint.props?.Code;
            const pointStyle = getIsoPointStyleFromCode(pointStyleCode);
            if (pointStyle) {
                const canvas = createIsoSymbolCanvas(pointStyle, getRgb(color), 12);
                shape = new Konva.Image({
                    name: 'model_' + model.modelId,
                    image: canvas,
                    x: x - canvas.width / 2,
                    y: y - canvas.height / 2,
                    width: canvas.width,
                    height: canvas.height,
                    listening: false,
                });
            } else {
                shape = new Konva.Circle({
                    name: 'model_' + model.modelId,
                    x: x,
                    y: y,
                    stroke: getRgb(color),
                    fill: getRgb(color),
                    radius: 2,
                    listening: false,
                });
            }
        }

        const hitbox = new Konva.Circle({
            name: 'model_' + model.modelId,
            x: x,
            y: y,
            stroke: 'transparent',
            radius: 5,
        });

        hitbox.on('click tap', function (e) {
            if (modelPoint.props && modelPoint.props.tutkimustapa) {
                e.evt.stopPropagation();
                e.evt.preventDefault();
                csCanvas.canvasWrapperEl.style.cursor = 'default';
                csCanvas.mouseDown = false;

                const fullPointData = { ...modelPoint.point, props: modelPoint.props };
                handleTekPointClick(modelPoint.props, fullPointData);
            } else {
                onClickCallback(modelPoint.point, x, y);
                showVertexTooltip(modelPoint.point, x, y, opts.model.name, modelPoint.name, modelPoint.props, csCanvas, true);
            }
        });

        hitbox.on('mouseover', function () {
            if (modelPoint.props && modelPoint.props.tutkimustapa) {
                csCanvas.canvasWrapperEl.style.cursor = 'pointer';
            } else {
                showVertexTooltip(modelPoint.point, x, y, opts.model.name, modelPoint.name, modelPoint.props, csCanvas, false);
            }
        });

        hitbox.on('mouseout', function () {
            // Reset cursor
            csCanvas.canvasWrapperEl.style.cursor = 'default';
            csCanvas.clearHoverTooltips();
        });

        // Add the hitbox first, then shape if it exists
        group.add(hitbox);
        if (shape) {
            group.add(shape);
        }
    });

    csCanvas.contentLayer.add(group);
}

function showLineTooltip(modelName: string, lineName: string, x: number, y: number, csCanvas: CrossSectionCanvas, sticky = false) {
    const text = [modelName, lineName].filter(Boolean).join('\n');
    csCanvas.drawTooltipAtMousePosition(text, -25, sticky);
}

function showVertexTooltip(point: any, x: number, y: number, modelName: string, lineName: string, props: Record<string, any>, csCanvas: CrossSectionCanvas, sticky = false) {
    const text = [
        modelName,
        lineName,
        kuura.getMessage('js.cross_section.b') + ': ' + point.x.toFixed(2),
        kuura.getMessage('js.cross_section.z') + ': ' + point.y.toFixed(2),
    ].filter(Boolean);

    if (props && props.tutkimustapa) {
        if (sticky) {
            const fullPointData = { ...point, props };
            handleTekPointClick(props, fullPointData);
        }
        return;
    }

    if (props) {
        if (Object.keys(props).length > 0) {
            text.push('\n');
        }
        Object.entries(props).forEach(([key, val]) => {
            text.push(key + ': ' + val);
        });
    }

    csCanvas.tooltipLayer.add(
        new Konva.Rect({
            x: x - 4,
            y: y - 4,
            width: 9,
            height: 9,
            stroke: 'gray',
            strokeWidth: 1,
            listening: false,
        })
    );
    csCanvas.drawTooltip(x, y - 15, text.join('\n'), sticky);
}

const extractSoilLayers = (pointProps: any): Array<{ depth: number; soilType: string; isEM: boolean }> => {
    const layers: Array<{ depth: number; soilType: string; isEM: boolean }> = [];
    const surface = pointProps.XY?.split(/\s+/)[2] || '0';
    const { measurements } = parseDataAndCalculateEndPoint(pointProps, surface);
    let previousSoilType = '';

    measurements.forEach(measurement => {
        let soilType = measurement.soilType || '';
        let isEM = false;

        if (soilType === '' && measurement.extraData) {
            for (const extra of measurement.extraData) {
                if (extra.type === 'EM') {
                    soilType = extra.data;
                    isEM = true;
                    break;
                }
            }
        }

        if (soilType !== '' && (soilType !== previousSoilType || isEM)) {
            layers.push({
                depth: measurement.depth,
                soilType: soilType,
                isEM: isEM,
            });
            previousSoilType = soilType;
        }
    });

    return layers;
};

const drawSoilLayerLabels = (opts: {
    modelPoint: any;
    x: number;
    y: number;
    barTop: number;
    barBottom: number;
    clampedTop: number;
    clampedBottom: number;
    biggestEntry: any;
    csCanvas: CrossSectionCanvas;
    group: any;
}) => {
    const { modelPoint, x, y, barTop, barBottom, clampedTop, clampedBottom, biggestEntry, csCanvas, group } = opts;
    const canvasOptions = csCanvas.canvasOptions;

    const soilLayers = extractSoilLayers(modelPoint.props);
    if (soilLayers.length === 0) return;

    const minFontSize = 7;
    const maxFontSize = 10;
    const minZoom = 5;
    const maxZoom = 100;
    const zoomFactor = Math.min(1, Math.max(0, (canvasOptions.y_scale - minZoom) / (maxZoom - minZoom)));
    const fontSize = minFontSize + (maxFontSize - minFontSize) * zoomFactor;

    interface LabelData {
        soilType: string;
        depth: number;
        originalY: number;
        adjustedY: number;
    }

    const labels: LabelData[] = [];
    let previousSoilType = '';

    soilLayers.forEach(layer => {
        if (layer.isEM || layer.soilType !== previousSoilType) {
            const layerY = csCanvas.metersToScreenCoordsY(modelPoint.point.y - layer.depth);
            labels.push({
                soilType: layer.soilType,
                depth: layer.depth,
                originalY: layerY,
                adjustedY: layerY,
            });
            previousSoilType = layer.soilType;
        }
    });

    if (labels.length === 0) return;

    labels.sort((a, b) => a.originalY - b.originalY);

    const minSpacing = fontSize + 3;
    for (let i = 1; i < labels.length; i++) {
        const current = labels[i];
        const previous = labels[i - 1];
        if (current.adjustedY - previous.adjustedY < minSpacing) {
            current.adjustedY = previous.adjustedY + minSpacing;
        }
    }

    labels.forEach(label => {
        const clampedOriginalY = Math.max(clampedTop, Math.min(label.originalY, clampedBottom));
        const horizontalLine = new Konva.Line({
            points: [x - 5.5, clampedOriginalY, x + 5.5, clampedOriginalY],
            stroke: 'gray',
            strokeWidth: 0.5,
            listening: false,
        });
        group.add(horizontalLine);
    });

    labels.forEach(label => {
        const clampedOriginalY = Math.max(clampedTop, Math.min(label.originalY, clampedBottom));
        const labelText = `${label.soilType} ${label.depth.toFixed(1)}m`;

        const tempText = new Konva.Text({
            text: labelText,
            fontSize: fontSize,
            fontFamily: 'Arial',
        });
        const textWidth = tempText.width();
        const textHeight = tempText.height();

        const labelX = x + 12;
        const textX = labelX + 2;
        let textY = label.adjustedY - fontSize / 2;

        const labelTop = textY;
        const labelBottom = textY + textHeight;

        if (labelBottom < canvasOptions.top_margin || labelTop > csCanvas.height - canvasOptions.bottom_margin) {
            return;
        }

        if (labelTop < canvasOptions.top_margin) {
            textY = canvasOptions.top_margin;
        } else if (labelBottom > csCanvas.height - canvasOptions.bottom_margin) {
            textY = csCanvas.height - canvasOptions.bottom_margin - textHeight;
        }

        const labelRight = textX + textWidth;
        if (labelRight > csCanvas.width - canvasOptions.side_margin) {
            return;
        }

        const finalAdjustedY = textY + fontSize / 2;

        const line = new Konva.Line({
            points: [x + 5.5, clampedOriginalY, labelX, finalAdjustedY],
            stroke: 'gray',
            strokeWidth: 0.5,
            listening: false,
        });
        group.add(line);

        const text = new Konva.Text({
            x: textX,
            y: textY,
            text: labelText,
            fontSize: fontSize,
            fontFamily: 'Arial',
            fill: 'black',
            listening: false,
        });
        group.add(text);
    });
};

const drawTekBar = (opts: { modelPoint: any; model: any; x: number; y: number; color: string; csCanvas: CrossSectionCanvas; group: any }) => {
    const { modelPoint, model, x, y, color, csCanvas, group } = opts;
    const canvasOptions = csCanvas.canvasOptions;
    const tt = modelPoint.props.tutkimustapa.split(/\s+/)[0];

    const biggestEntry = Object.entries(modelPoint.props)
        .filter(([key, value]) => {
            if (isNaN(Number(key)) || key.trim() === '') return false;
            const firstPart = String(value).split(/\s+/)[0];
            return !isNaN(Number(firstPart)) && firstPart.trim() !== '';
        })
        .reduce((max, [key, value]) => (Number(key) > max.key ? { key: Number(key), value: parseFloat(String(value).split(/\s+/)[0]) } : max), {
            key: -Infinity,
            value: 0,
        });

    const barTop = Math.min(y, csCanvas.metersToScreenCoordsY(modelPoint.point.y - biggestEntry.value));
    const barBottom = Math.max(y, csCanvas.metersToScreenCoordsY(modelPoint.point.y - biggestEntry.value));

    // Skip only if the entire bar (including bottom) is outside bounds
    if (y < canvasOptions.top_margin && barBottom < canvasOptions.top_margin) {
        return;
    }
    if (y > csCanvas.height - canvasOptions.bottom_margin && barBottom > csCanvas.height - canvasOptions.bottom_margin) {
        return;
    }
    const clampedTop = Math.max(canvasOptions.top_margin, barTop);
    const clampedBottom = Math.min(barBottom, csCanvas.height - canvasOptions.bottom_margin);
    const isTruncatedAtBottom = barBottom > csCanvas.height - canvasOptions.bottom_margin;
    const isTruncatedAtTop = barTop < canvasOptions.top_margin;

    const rect = new Konva.Rect({
        x: x - 5,
        y: clampedTop,
        width: 10,
        height: Math.max(0, clampedBottom - clampedTop),
        fill: 'white',
        stroke: null,
        listening: false,
    });
    group.add(rect);

    if (!isTruncatedAtTop) {
        const topBorder = new Konva.Line({
            points: [x - 5.5, clampedTop + 0.5, x + 5.5, clampedTop + 0.5],
            stroke: getRgb(color),
            strokeWidth: 1,
            listening: false,
        });
        group.add(topBorder);
    }

    const leftBorder = new Konva.Line({
        points: [x - 5.5, clampedTop, x - 5.5, clampedBottom],
        stroke: getRgb(color),
        strokeWidth: 1,
        listening: false,
    });

    const rightBorder = new Konva.Line({
        points: [x + 5.5, clampedTop, x + 5.5, clampedBottom],
        stroke: getRgb(color),
        strokeWidth: 1,
        listening: false,
    });

    group.add(leftBorder);
    group.add(rightBorder);

    if (!isTruncatedAtBottom) {
        const separator = new Konva.Line({
            points: [x - 9, clampedBottom - 0.5, x + 9, clampedBottom - 0.5],
            stroke: getRgb(color),
            strokeWidth: 1,
            listening: false,
        });
        group.add(separator);

        const triangle = new Konva.Line({
            points: [x - 4, clampedBottom + 8, x, clampedBottom, x + 4, clampedBottom + 8],
            closed: true,
            fill: getRgb(color),
            stroke: getRgb(color),
            strokeWidth: 1,
            listening: false,
        });
        group.add(triangle);

        const paattymistapa = modelPoint.props.paattymistapa || modelPoint.props.päättymistapa;
        if (paattymistapa) {
            const triangleHitbox = new Konva.Rect({
                x: x - 15,
                y: clampedBottom,
                width: 30,
                height: 25,
                fill: 'transparent',
                listening: true,
            });

            triangleHitbox.on('mouseover', function () {
                const toolTipYOffset = 30;
                csCanvas.drawTooltip(x, clampedBottom + toolTipYOffset, 'Päättymistapa: ' + paattymistapa, false);
            });

            triangleHitbox.on('mouseout', function () {
                csCanvas.clearHoverTooltips();
            });

            group.add(triangleHitbox);
        }
    }

    // Create a hitbox for the rectangle
    const rectHitbox = new Konva.Rect({
        name: 'model_' + model.modelId,
        x: x - 8,
        y: clampedTop - 2,
        width: 12,
        height: Math.max(0, clampedBottom - clampedTop) + 4,
        fill: 'transparent',
        listening: true,
    });

    rectHitbox.on('click tap', function (e) {
        e.evt.stopPropagation();
        e.evt.preventDefault();
        csCanvas.canvasWrapperEl.style.cursor = 'default';
        csCanvas.mouseDown = false;
        const fullPointData = { ...modelPoint.point, props: modelPoint.props };
        handleTekPointClick(modelPoint.props, fullPointData);
    });

    rectHitbox.on('mouseover', function () {
        csCanvas.canvasWrapperEl.style.cursor = 'pointer';
    });

    rectHitbox.on('mouseout', function () {
        csCanvas.canvasWrapperEl.style.cursor = 'default';
        csCanvas.clearHoverTooltips();
    });

    group.add(rectHitbox);

    drawSoilLayerLabels({
        modelPoint,
        x,
        y,
        barTop,
        barBottom,
        clampedTop,
        clampedBottom,
        biggestEntry,
        csCanvas,
        group,
    });

    // Add SVG icon on top of the bar only if point is within bounds
    if (y >= canvasOptions.top_margin && y <= csCanvas.height - canvasOptions.bottom_margin) {
        const imageObj = getTekPointImage(tt, img => {
            const imageShape = new Konva.Image({
                name: 'model_' + model.modelId,
                image: img,
                x: x - 7,
                y: y - 7,
                width: 14,
                height: 14,
                listening: false,
            });
            group.add(imageShape);
            csCanvas.contentLayer.draw();
        });

        if (imageObj.complete) {
            const imageShape = new Konva.Image({
                name: 'model_' + model.modelId,
                image: imageObj,
                x: x - 7,
                y: y - 7,
                width: 14,
                height: 14,
                listening: false,
            });
            group.add(imageShape);
        }
    }
};

const handleTekPointClick = (pointData: any, fullPoint?: any) => {
    const tt = pointData['tutkimustapa'];
    if (!tt) {
        console.log('No TT found in point data');
        return;
    }

    const parsedValue = tt.split(/[\s-]/)[0];
    const title = SURVEY_TYPE_TITLES[parsedValue];
    if (title) {
        showTekPointSoilSurvey({
            ...pointData,
            title,
            point: fullPoint,
        });
    } else {
        console.log('Unknown TT type:', parsedValue);
    }
};

function drawBorder(border: any, csCanvas: CrossSectionCanvas) {
    const point = border.point;
    const center = csCanvas.getCenter();
    const canvasOptions = csCanvas.canvasOptions;
    const x = center + point.x * canvasOptions.x_scale;
    const y0 = canvasOptions.top_margin;
    const y1 = csCanvas.height - canvasOptions.bottom_margin;

    const line = new Konva.Line({
        name: 'model_' + point.id,
        points: [x, y0, x, y1],
        stroke: getRgb(border.color),
        strokeWidth: 1,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
    });

    csCanvas.contentLayer.add(line);
}

const getRgb = (color: string) => 'rgb(' + color + ')';

export const CrossSectionModel = {
    drawModel,
    drawBorder,
};
